/**
 * A5 — offline-aware auth: the pure, dependency-free decision + the small
 * localStorage-backed state it needs. No React/Firebase imports so it is fully
 * unit-testable (and a safe leaf dependency for any module).
 *
 * Background (verified against the Firebase SDK source): a purely-offline token
 * expiry does NOT fire onAuthStateChanged(null) — network refresh failures are
 * treated as transient and the user is kept. A non-intentional null while
 * offline therefore comes from a cross-tab signOut, IndexedDB eviction, or an
 * SDK init race. When one arrives offline we must NOT strand the vet at /login
 * (unreachable offline) nor wipe their replica; we keep the cached session
 * mounted for a bounded grace window and conclude a genuinely-dead session only
 * once reachability is confirmed (or the window expires). See
 * `git show 8a62cf805:Docs/planning/desktop-offline-a5-offline-auth.md`.
 */

/** How long a cached (network-backed) session may keep serving offline reads
 *  before re-auth is forced. Configurable; ratify the value in the A3 memo. */
export const OFFLINE_AUTH_GRACE_MS = 24 * 60 * 60 * 1000; // 24h

/** A logout marker older than this is treated as stale (leaked) and ignored. */
export const INTENT_TTL_MS = 60 * 1000; // 60s

const LAST_GOOD_AUTH_KEY = 'st.offline.lastGoodAuth';
const SIGNOUT_INTENT_KEY = 'st.offline.signOutIntent';
// v2 (P2): the permission array in this blob used to come from the Firebase ID
// token; it is now the effective set from GET /users/me. A blob written before
// P2 can name a catch-all ('view:all'/'manage:all') that P3 RETIRED — no role
// holds one and neither guard resolves one any more — and it survives the deploy
// in every browser that has ever been offline, so a returning user would be
// evaluated against a grant nobody holds, for as long as the cache lives.
// Versioning the key makes a stale blob IGNORED rather than half-honoured;
// clearClaimsCache() also removes the v1 key so it does not sit in localStorage
// forever.
//
// ⚠ ROLLBACK CONSEQUENCE, stated so nobody rediscovers it during an incident: a
// revert of P2's frontend reads 'st.offline.claims' (v1) again, which P2 never
// wrote — so every browser loses its offline claims FALLBACK until its next
// ONLINE mount re-caches under v1. Since P4, a frontend reverted past P2 finds no claim-borne
// permission array and fails closed until rolled forward; role + clinicId remain available. P4 also
// ships override rows, so a minted mirror would differ from the authoritative resolved set by design.
// The supported recovery is to roll the web forward and keep jwt.strategy's Postgres read authoritative.
const CLAIMS_KEY = 'st.offline.claims.v2';
const LEGACY_CLAIMS_KEYS = ['st.offline.claims'];

export type SessionEndDecision = 'grace' | 'end';

export interface SessionEndInput {
  /** true when the device is (confirmed) offline — see AuthProvider reachability probe */
  offline: boolean;
  /** true when this null event is an explicit user logout */
  intentional: boolean;
  /** ms timestamp of the last network-backed sign-in, or null if unknown */
  lastGoodAuth: number | null;
  now: number;
}

/**
 * Decide whether a non-`user` auth event should enter the offline grace window
 * or genuinely end the session. Grace requires ALL of: not an intentional
 * logout, confirmed offline, and a present + numeric + non-future + in-window
 * lastGoodAuth. Anything else fails safe to 'end'.
 */
export function decideSessionEnd({
  offline,
  intentional,
  lastGoodAuth,
  now,
}: SessionEndInput): SessionEndDecision {
  if (intentional) return 'end';
  if (!offline) return 'end';
  if (lastGoodAuth == null || typeof lastGoodAuth !== 'number' || Number.isNaN(lastGoodAuth)) {
    return 'end';
  }
  const age = now - lastGoodAuth;
  if (Number.isNaN(age) || age < 0) return 'end'; // garbage now, or clock rollback / future stamp
  if (age >= OFFLINE_AUTH_GRACE_MS) return 'end'; // window expired
  return 'grace';
}

// --- lastGoodAuth -----------------------------------------------------------

export function recordLastGoodAuth(now: number): void {
  try {
    localStorage.setItem(LAST_GOOD_AUTH_KEY, String(now));
  } catch {
    /* localStorage unavailable — grace simply won't engage; fail safe */
  }
}

export function getLastGoodAuth(): number | null {
  try {
    const raw = localStorage.getItem(LAST_GOOD_AUTH_KEY);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function clearLastGoodAuth(): void {
  try {
    localStorage.removeItem(LAST_GOOD_AUTH_KEY);
  } catch {
    /* ignore */
  }
}

// --- intentional-signout flag (uid-scoped, short-TTL one-shot) ---------------

export function markIntentionalSignOut(uid: string, now: number): void {
  try {
    localStorage.setItem(SIGNOUT_INTENT_KEY, JSON.stringify({ uid, ts: now }));
  } catch {
    /* ignore */
  }
}

/**
 * Return true iff a FRESH intentional-logout marker for THIS uid exists, then
 * clear it (one-shot). A stale (TTL-expired), wrong-uid, or malformed marker
 * returns false AND is cleared, so a leaked flag can never end an unrelated
 * later session.
 */
export function consumeIntentionalSignOut(uid: string, now: number): boolean {
  let parsed: { uid?: string; ts?: number } | null = null;
  try {
    const raw = localStorage.getItem(SIGNOUT_INTENT_KEY);
    if (raw != null) parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }
  // Always clear on read — one-shot, and self-heals garbage/stale/wrong-uid.
  clearIntentionalSignOut();
  if (!parsed || typeof parsed.ts !== 'number') return false;
  if (parsed.uid !== uid) return false;
  if (now - parsed.ts > INTENT_TTL_MS || now < parsed.ts) return false;
  return true;
}

export function clearIntentionalSignOut(): void {
  try {
    localStorage.removeItem(SIGNOUT_INTENT_KEY);
  } catch {
    /* ignore */
  }
}

// --- claims cache (uid-keyed authz-metadata fallback for offline useRBAC) -----

export interface CachedClaims {
  role: string;
  clinicId: string;
  permissions: string[];
}

export function cacheClaims(claims: CachedClaims & { uid: string }): void {
  try {
    localStorage.setItem(
      CLAIMS_KEY,
      JSON.stringify({
        uid: claims.uid,
        role: claims.role,
        clinicId: claims.clinicId,
        permissions: claims.permissions,
      }),
    );
  } catch {
    /* ignore */
  }
}

/** Cached authz claims for `uid`, or null if absent/garbage/for a different uid. */
export function getCachedClaims(uid: string): CachedClaims | null {
  try {
    const raw = localStorage.getItem(CLAIMS_KEY);
    if (raw == null) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.uid !== uid) return null;
    return {
      role: parsed.role,
      clinicId: parsed.clinicId,
      permissions: Array.isArray(parsed.permissions) ? parsed.permissions : [],
    };
  } catch {
    return null;
  }
}

export function clearClaimsCache(): void {
  try {
    localStorage.removeItem(CLAIMS_KEY);
    LEGACY_CLAIMS_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

// --- reauth-to-sync hint (sessionStorage; explains a post-grace bounce to /login) ---

const REAUTH_HINT_KEY = 'st.offline.reauthToSync';

/** Mark that a NON-intentional session end just occurred while there may be
 *  unsynced work — Login reads this to show "sign in to sync" rather than a
 *  bare login screen. sessionStorage so it's per-tab and dies with the tab. */
export function setReauthHint(): void {
  try {
    sessionStorage.setItem(REAUTH_HINT_KEY, '1');
  } catch {
    /* ignore */
  }
}

/** True iff a reauth hint is pending; clears it (one-shot). */
export function consumeReauthHint(): boolean {
  try {
    const v = sessionStorage.getItem(REAUTH_HINT_KEY);
    sessionStorage.removeItem(REAUTH_HINT_KEY);
    return v === '1';
  } catch {
    return false;
  }
}
